import apiClient from './client'

export const sessionsApi = {
  getSessions: (params?: { status?: string; page?: number }) =>
    apiClient.get('/sessions', { params }).then((r) => r.data.data ?? r.data),

  getSession: (id: number) =>
    apiClient.get(`/sessions/${id}`).then((r) => r.data),

  getBooking: (id: number) =>
    apiClient.get(`/bookings/${id}`).then((r) => r.data.data ?? r.data),

  rescheduleSession: (id: number, scheduled_at: string) =>
    apiClient
      .put(`/sessions/${id}/reschedule`, { scheduled_at })
      .then((r) => r.data),

  cancelSession: (id: number, reason?: string) =>
    apiClient
      .put(`/sessions/${id}/cancel`, { reason })
      .then((r) => r.data),

  cancelBooking: (id: number, reason?: string) =>
    apiClient
      .put(`/bookings/${id}/cancel`, { reason })
      .then((r) => r.data),

  rescheduleBooking: (id: number, scheduled_at: string) =>
    apiClient
      .put(`/bookings/${id}/reschedule`, { scheduled_at })
      .then((r) => r.data),

  rescheduleBookingFull: (id: number, data: {
    new_slot_start_utc: string
    new_interviewer_id?: number
    job_description_text?: string
    job_description_path?: string
    special_notes?: string
    target_role?: string
    interview_goals?: string
  }) =>
    apiClient
      .put(`/bookings/${id}/reschedule-full`, data)
      .then((r) => r.data),

  getClientInterviews: (tab: 'upcoming' | 'completed') =>
    apiClient
      .get('/client/interviews', { params: { tab } })
      .then((r) => r.data.data ?? r.data),

  getRecording: (id: number) =>
    apiClient
      .get(`/client/interviews/${id}/recording`)
      .then((r) => r.data),

  getBookingRecording: (id: number) =>
    apiClient
      .get(`/client/bookings/${id}/recording`)
      .then((r) => r.data),

  getFeedback: () =>
    apiClient.get('/feedback').then((r) => r.data),

  getFeedbackById: (id: number) =>
    apiClient.get(`/feedback/${id}`).then((r) => r.data),

  submitRating: (
    source: 'session' | 'booking',
    sourceId: number,
    score: number,
    comment?: string,
  ) =>
    apiClient
      .post('/ratings', {
        ...(source === 'session' ? { session_id: sourceId } : { booking_id: sourceId }),
        score,
        comment,
      })
      .then((r) => r.data),

  getRatingForSession: (sessionId: number) =>
    apiClient
      .get(`/ratings/session/${sessionId}`)
      .then((r) => r.data.data ?? r.data),

  getRatingForBooking: (bookingId: number) =>
    apiClient
      .get(`/ratings/booking/${bookingId}`)
      .then((r) => r.data.data ?? r.data),

  getPaymentHistory: () =>
    apiClient.get('/client/payments').then((r) => r.data),

  getStats: () =>
    apiClient.get('/stats').then((r) => r.data),
}

export const bookingApi = {
  getInterviewers: (params?: { page?: number; per_page?: number }) =>
    apiClient.get('/interviewers', { params }).then((r) => r.data.data ?? r.data),

  getCalendlyAvailability: (date: string, timezone?: string, language?: string) =>
    apiClient
      .get('/calendly/availability/all', {
        params: {
          date,
          timezone: timezone || 'UTC',
          ...(language ? { language } : {}),
        },
      })
      .then((r) => r.data.data ?? r.data),

  bookCalendlySlot: (data: {
    interviewer_id: number
    start_utc: string
    invitee_name: string
    invitee_email: string
  }) =>
    apiClient.post('/calendly/book', data).then((r) => r.data),

  uploadJobDescriptionPdf: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return apiClient
      .post('/calendly/job-description/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data.data ?? r.data) as Promise<{ path: string; filename: string }>
  },

  initiateBookingPayment: (data: {
    interviewer_id: number
    start_utc: string
    invitee_name: string
    invitee_email: string
    job_description_path?: string
    job_description_text?: string
    special_notes?: string
    target_role?: string
    interview_goals?: string
  }) =>
    apiClient
      .post('/payments/booking/initiate', data)
      .then((r) => r.data.data ?? r.data),

  getMySchedule: () =>
    apiClient
      .get('/calendly/my-schedule')
      .then((r) => r.data.data ?? r.data),

  verifyCheckoutSession: (checkoutSessionId: string) =>
    apiClient
      .post('/payments/checkout/verify', { checkout_session_id: checkoutSessionId })
      .then((r) => r.data),

  createPaymentIntent: (data: {
    interviewer_id: number
    scheduled_at: string
    title: string
    description?: string
  }) =>
    apiClient
      .post('/payments/create-intent', data)
      .then((r) => r.data),

  getStripeConfig: () =>
    apiClient.get('/payments/config').then((r) => r.data),

  createBooking: (data: {
    payment_intent_id: string
    interviewer_id: number
    scheduled_at: string
    title: string
    description?: string
  }) =>
    apiClient.post('/bookings/create', data).then((r) => r.data),
}
